import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { TextField } from "@/components/Interface/TextField/TextFields";
import { TwoFactorCodeModal } from "@/components/Portal/UserAccount/Security/TwoFactorCodeModal";
import { VerifyEmailModal } from "@/components/Welcome/VerifyEmailModal";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerLogin } from "@/hooks/useCustomerLogin";
import { FCC } from "@/types";
import { FORGOT_PASSWORD_URL } from "@/utils/apiUrls";
import {
  createLoginErrorHandler,
  LOGIN_ERROR_MESSAGES,
  LoginFormFields,
} from "@/utils/loginErrorHandler";

import { MagicLinkEmailSent } from "./MagicLinkEmailSent";
import { MagicLinkRequestForm } from "./MagicLinkRequestForm";

type LoginView = "password" | "magic-link-request" | "magic-link-sent";

interface Props {
  joinTribeOnLogin?: () => void;
  /** Magic link login is only enabled on the main welcome screen. */
  enableMagicLinkLogin?: boolean;
}

export const LogInForm: FCC<Props> = ({
  children,
  joinTribeOnLogin,
  enableMagicLinkLogin = false,
}) => {
  const [loginView, setLoginView] = useState<LoginView>("password");
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = useForm<LoginFormFields>({
    defaultValues: {
      emailAddress: "",
      password: "",
    },
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [forceEnableButton, setForceEnableButton] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    isLoading,
    shouldShowTwoFactorModal,
    shouldShowVerifyEmailModal,
    twoFactorMethod,
    twoFactorGrantToken,
    loginAsCustomer,
    setIsLoading,
    setShouldShowTwoFactorModal,
    setShouldShowVerifyEmailModal,
  } = useCustomerLogin({
    onSuccess: () => {
      // If VerifyEmailModal is shown we let it handle the onSuccess case
      if (shouldShowVerifyEmailModal) {
        return;
      }

      // If TwoFactorModal is shown we let it handle the onSuccess case
      if (shouldShowTwoFactorModal) {
        return;
      }

      handleAuthSuccess();
    },
    onError: (error) => {
      // Create error handler with current 2FA modal state
      const errorHandler = createLoginErrorHandler(
        setError,
        t,
        showAlert,
        "emailAddress",
      );
      errorHandler(error);
    },
  });

  useEffect(() => {
    // If button is disabled, set a 10-second timer to force reenable it
    if (isLoading || isSubmitting) {
      setForceEnableButton(false);
      const timer = setTimeout(() => {
        setForceEnableButton(true);
      }, 10000);

      return () => clearTimeout(timer);
    } else {
      // Reset force enable when button is naturally enabled
      setForceEnableButton(false);
    }
  }, [isLoading, isSubmitting]);

  const onSubmit = async (data: LoginFormFields) => {
    setSubmitting(true);

    try {
      await loginAsCustomer({
        email: data.emailAddress,
        password: data.password,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const email = watch("emailAddress");
  const password = watch("password");

  const handleAuthSuccess = () => {
    if (joinTribeOnLogin) {
      setTimeout(() => {
        joinTribeOnLogin();
        navigate("/portal/friends-with-benefits/group");
      }, 3000);
    } else {
      navigate("/portal/cockpit/tiles");
    }
  };

  const resetToPasswordLogin = () => setLoginView("password");

  if (enableMagicLinkLogin && loginView === "magic-link-request") {
    return (
      <MagicLinkRequestForm
        onBack={resetToPasswordLogin}
        onSuccess={() => setLoginView("magic-link-sent")}
      />
    );
  }

  if (enableMagicLinkLogin && loginView === "magic-link-sent") {
    return <MagicLinkEmailSent onBack={resetToPasswordLogin} />;
  }

  return (
    <form
      className="max-w-[420px] md:m-auto md:h-max md:w-full"
      onSubmit={handleSubmit(onSubmit)}
    >
      <h1 className="mb-10 text-4xl font-semibold text-white">
        {t("translation:newWelcome.logInForm.title")}
      </h1>

      <TextField
        id="emailAddress"
        error={errors.emailAddress}
        inputType="email"
        labelText={t("translation:Common.generalForm.fields.email.label")}
        placeholder={t(
          "translation:Common.generalForm.fields.email.placeholder",
        )}
        register={register("emailAddress", {
          required: t(
            "translation:Common.generalForm.validation.emailRequired",
          ),
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: t(
              "translation:Common.generalForm.validation.emailNotValid",
            ),
          },
        })}
      />
      <TextField
        id="password"
        labelText={t("translation:Common.generalForm.fields.password.label")}
        error={errors.password}
        inputType="password"
        placeholder={t(
          "translation:Common.generalForm.fields.password.placeholder",
        )}
        register={register("password", {
          required: t(
            "translation:Common.generalForm.validation.passwordRequired",
          ),
        })}
      />
      <div className="mb-10 mt-4 flex w-full flex-row justify-between lg:justify-start">
        <a
          aria-label="forgot password link"
          href={FORGOT_PASSWORD_URL.toString()}
          target="_self"
          rel="noreferrer"
          className="inline cursor-pointer text-xs font-semibold text-white focus-within:underline hover:underline focus:underline focus-visible:underline active:underline"
        >
          {t("translation:newWelcome.logInForm.forgotPassword")}
        </a>
      </div>

      {shouldShowVerifyEmailModal && (
        <VerifyEmailModal
          email={email}
          password={password}
          handleSuccess={() => {
            setShouldShowVerifyEmailModal(false);

            // Trigger the navigation that was being skipped by the onSuccess early return
            handleAuthSuccess();
          }}
          handleClose={() => {
            setShouldShowVerifyEmailModal(false);
            setSubmitting(false);
            setIsLoading(false);

            reset({
              emailAddress: "",
              password: "",
            });
          }}
        />
      )}

      {shouldShowTwoFactorModal && (
        <TwoFactorCodeModal
          userName={email}
          userPassword={password}
          setShowModal={setShouldShowTwoFactorModal}
          method={twoFactorMethod}
          successAction={() => handleAuthSuccess()}
          type="login"
          twoFactorGrantToken={twoFactorGrantToken}
          onAccountLocked={() => {
            const accountLockedMessage = t(LOGIN_ERROR_MESSAGES.ACCOUNT_LOCKED);

            setError("emailAddress", { message: accountLockedMessage });
            setError("password", { message: accountLockedMessage });
          }}
        />
      )}

      <Button
        fullWidth
        className="accent mb-4"
        type="submit"
        disabled={!forceEnableButton && (isLoading || submitting)}
      >
        {t("translation:newWelcome.logInForm.submitButton")}
      </Button>

      {enableMagicLinkLogin && (
        <div className="mb-6 flex justify-center">
          <button
            type="button"
            onClick={() => setLoginView("magic-link-request")}
            className="inline cursor-pointer text-sm font-semibold text-highlight-100 underline focus-within:underline hover:underline focus:underline focus-visible:underline active:underline"
            data-testid="magic-link-login-trigger"
          >
            {t("translation:newWelcome.magicLink.linkOnLogin")}
          </button>
        </div>
      )}

      {children}
    </form>
  );
};
